import { trackCoachEvent } from "@/lib/coachEventLogger";

export type CoachVeeraTelemetrySurface = "coach_post_meeting" | "coach_session_prep";
export type CoachVeeraEntryPoint = "save_and_open" | "prep_brief" | "auto_open";
export type CoachVeeraEvidenceSource =
  | "coach_veera_answer"
  | "coach_veera_brief"
  | "coach_veera_claim";
export type CoachVeeraVoiceCustomizationStatus =
  | "default"
  | "framework_default"
  | "customized";

function buildMeasurementProperties(params: {
  surface: CoachVeeraTelemetrySurface;
  interventionType: "open_context" | "freeform_question" | "suggested_prompt" | "evidence_opened";
  frameworkPackId?: string | null;
  voiceCustomizationStatus?: CoachVeeraVoiceCustomizationStatus;
}) {
  const properties: Record<string, string> = {
    surface: params.surface,
    role_side: "coach",
    intervention_type: params.interventionType,
    voice_customization_status: params.voiceCustomizationStatus || "default",
  };

  if (params.frameworkPackId?.trim()) {
    properties.framework_pack_id = params.frameworkPackId.trim();
  }

  return properties;
}

function bucketQuestionLength(question: string): "short" | "medium" | "long" {
  const length = question.trim().length;
  if (length <= 24) return "short";
  if (length <= 80) return "medium";
  return "long";
}

function bucketPromptLength(prompt: string): "short" | "medium" | "long" {
  const length = prompt.trim().length;
  if (length <= 32) return "short";
  if (length <= 96) return "medium";
  return "long";
}

function buildResponseEventId(
  responseId: string,
  eventName: "coach_veera_question_submitted" | "coach_veera_prompt_selected",
): string | undefined {
  const compact = responseId.trim().toLowerCase().replaceAll("-", "");
  if (!/^[0-9a-f]{32}$/.test(compact)) return undefined;
  const namespace = eventName === "coach_veera_question_submitted"
    ? "715ab2388dfe4c7aa8d0492a3c4ddf01"
    : "b5615dd5fe6e48cbbfc9db22f2afb305";
  const mixed = Array.from({ length: 32 }, (_, index) => (
    Number.parseInt(compact[index], 16) ^ Number.parseInt(namespace[index], 16)
  ).toString(16));
  mixed[12] = "5";
  mixed[16] = ((Number.parseInt(mixed[16], 16) & 0x3) | 0x8).toString(16);
  const hex = mixed.join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export async function trackCoachVeeraOpened(params: {
  coachId: string;
  leaderId: string;
  surface: CoachVeeraTelemetrySurface;
  entryPoint: CoachVeeraEntryPoint;
  transcriptId?: string | null;
  meetingId?: string | null;
  frameworkPackId?: string | null;
  voiceCustomizationStatus?: CoachVeeraVoiceCustomizationStatus;
}): Promise<void> {
  const properties: Record<string, string> = {
    ...buildMeasurementProperties({
      surface: params.surface,
      interventionType: "open_context",
      frameworkPackId: params.frameworkPackId,
      voiceCustomizationStatus: params.voiceCustomizationStatus,
    }),
    entry_point: params.entryPoint,
  };

  if (params.transcriptId?.trim()) {
    properties.transcript_id = params.transcriptId.trim();
  }
  if (params.meetingId?.trim()) {
    properties.meeting_id = params.meetingId.trim();
  }

  await trackCoachEvent({
    coachId: params.coachId,
    leaderId: params.leaderId,
    eventName: "coach_veera_opened",
    properties,
  });
}

export async function trackCoachVeeraQuestionSubmitted(params: {
  coachId: string;
  leaderId: string;
  surface: CoachVeeraTelemetrySurface;
  sessionId?: string | null;
  question: string;
  historyScope: "rolling_90d" | "rolling_365d";
  hasExistingSession: boolean;
  responseId: string;
  frameworkPackId?: string | null;
  voiceCustomizationStatus?: CoachVeeraVoiceCustomizationStatus;
}): Promise<void> {
  const properties: Record<string, string | boolean> = {
    ...buildMeasurementProperties({
      surface: params.surface,
      interventionType: "freeform_question",
      frameworkPackId: params.frameworkPackId,
      voiceCustomizationStatus: params.voiceCustomizationStatus,
    }),
    question_length_bucket: bucketQuestionLength(params.question),
    history_scope: params.historyScope,
    has_existing_session: params.hasExistingSession,
    response_id: params.responseId,
  };

  if (params.sessionId?.trim()) {
    properties.session_id = params.sessionId.trim();
  }

  await trackCoachEvent({
    coachId: params.coachId,
    leaderId: params.leaderId,
    eventName: "coach_veera_question_submitted",
    properties,
    eventId: buildResponseEventId(params.responseId, "coach_veera_question_submitted"),
  });
}

export async function trackCoachVeeraPromptSelected(params: {
  coachId: string;
  leaderId: string;
  surface: CoachVeeraTelemetrySurface;
  prompt: string;
  promptIndex: number;
  responseId: string;
  frameworkPackId?: string | null;
  voiceCustomizationStatus?: CoachVeeraVoiceCustomizationStatus;
}): Promise<void> {
  await trackCoachEvent({
    coachId: params.coachId,
    leaderId: params.leaderId,
    eventName: "coach_veera_prompt_selected",
    properties: {
      ...buildMeasurementProperties({
        surface: params.surface,
        interventionType: "suggested_prompt",
        frameworkPackId: params.frameworkPackId,
        voiceCustomizationStatus: params.voiceCustomizationStatus,
      }),
      prompt_index: params.promptIndex,
      prompt_length_bucket: bucketPromptLength(params.prompt),
      response_id: params.responseId,
    },
    eventId: buildResponseEventId(params.responseId, "coach_veera_prompt_selected"),
  });
}

export async function trackCoachVeeraEvidenceOpened(params: {
  coachId: string;
  leaderId: string;
  surface: CoachVeeraTelemetrySurface;
  evidenceSource: CoachVeeraEvidenceSource;
  label: string;
  route?: string | null;
  frameworkPackId?: string | null;
  voiceCustomizationStatus?: CoachVeeraVoiceCustomizationStatus;
}): Promise<void> {
  await trackCoachEvent({
    coachId: params.coachId,
    leaderId: params.leaderId,
    eventName: "coach_veera_evidence_opened",
    properties: {
      ...buildMeasurementProperties({
        surface: params.surface,
        interventionType: "evidence_opened",
        frameworkPackId: params.frameworkPackId,
        voiceCustomizationStatus: params.voiceCustomizationStatus,
      }),
      evidence_source: params.evidenceSource,
      reference_label: params.label,
      has_route: Boolean(params.route?.trim()),
    },
  });
}
